import { useCallback, useContext, useEffect } from 'react';
import type { MultiValue, SingleValue } from 'react-select';
import SelectAsync from 'react-select/async';
import type { CustomFilter } from '@trinity/chat';
import { AuthorContext } from '@trinity/chat-components/components/Chat/context';
import { useAgentResources } from '@trinity/chat-components/components/Chat.Header/AgentResources.Container.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { CustomFilterMultiselect } from './CustomFilter.Multiselect';
import type { AgentFilterItem, FilterData } from './interfaces';
import { FilterTypeEnum } from './interfaces';

type Props = {
  agentIdentifier: string;
  filter: AgentFilterItem;
  values: CustomFilter.ItemValue[];
  onValuesChange: (values: CustomFilter.ItemValue[]) => void;
};

export const AgentCustomFilterValueSelector = ({ agentIdentifier, filter, values, onValuesChange }: Props) => {
  if (filter.type === FilterTypeEnum.Autocomplete) {
    return (
      <AutoCompleteFilterValueSelector
        agentIdentifier={agentIdentifier}
        filter={filter}
        values={values}
        onValuesChange={onValuesChange}
      />
    );
  } else if (filter.type === FilterTypeEnum.UserTerritory) {
    return (
      <UserTerritoryFilterValueSelector
        agentIdentifier={agentIdentifier}
        filter={filter}
        values={values}
        onValuesChange={onValuesChange}
      />
    );
  } else if (filter.type === FilterTypeEnum.Dropdown || filter.type === FilterTypeEnum.DateRange) {
    const filterData = filter.data as FilterData<FilterTypeEnum.Dropdown | FilterTypeEnum.DateRange>;
    const availableValues = filterData.items.map(item => ({
      id: item.key,
      name: item.value,
    }));
    return (
      <CustomFilterMultiselect
        placeholder="Select values..."
        options={availableValues}
        value={values}
        onSelect={onValuesChange}
      />
    );
  }

  return null;
};


const AutoCompleteFilterValueSelector = (props: Props) => {

  const apiClient = useChatApiClient();

  const loadOptions = useCallback(((inputValue: string) => {
    return apiClient.agentResources.searchAgentFilter({ agentIdentifier: props.agentIdentifier, filterId: props.filter.id, query: inputValue })
      .then(response => response.results.map<CustomFilter.ItemValue>(item => ({
        id: item.id.toString(),
        name: item.name,
      })));
  }), [apiClient, props.agentIdentifier, props.filter.id]);

  return (
    <SelectAsync<CustomFilter.ItemValue, true>
      loadOptions={loadOptions}
      value={props.values}
      getOptionLabel={option => option.name}
      getOptionValue={option => option.id}
      onChange={(selectedValues: MultiValue<CustomFilter.ItemValue>) => {
        props.onValuesChange([...selectedValues]);
      }}
      isMulti
      placeholder="Type to search..." />
  );
};

const UserTerritoryFilterValueSelector = (props: Props) => {
  const author = useContext(AuthorContext);
  const agentResources = useAgentResources();
  const values = props.values;
  const onValuesChange = props.onValuesChange;
  const territoryOptions = agentResources.territoryOptions;
  const hasLoadedTerritories = agentResources.hasLoadedTerritories;

  const loadOptions = useCallback((inputValue: string) => {
    const normalizedInputValue = inputValue.trim().toLowerCase();
    const filteredOptions = territoryOptions.filter((option) => {
      if (normalizedInputValue.length === 0) {
        return true;
      }

      return (
        option.name.toLowerCase().includes(normalizedInputValue) ||
        option.id.toLowerCase().includes(normalizedInputValue)
      );
    });

    return Promise.resolve(filteredOptions);
  }, [territoryOptions]);

  const selectedValue = values.length > 0 ? values[0] : null;

  useEffect(() => {
    if (values.length > 0) {
      return;
    }

    if (!hasLoadedTerritories) {
      return;
    }

    const defaultTerritoryId = author?.metadata?.defaultTerritoryId;
    if (typeof defaultTerritoryId !== 'string' || defaultTerritoryId.length === 0) {
      return;
    }

    const defaultTerritoryOption = territoryOptions.find((option) => option.id === defaultTerritoryId);
    if (!defaultTerritoryOption) {
      return;
    }

    onValuesChange([defaultTerritoryOption]);
  }, [
    author?.metadata?.defaultTerritoryId,
    hasLoadedTerritories,
    onValuesChange,
    territoryOptions,
    values.length,
  ]);

  return (
    <SelectAsync<CustomFilter.ItemValue, false>
      defaultOptions={territoryOptions}
      loadOptions={loadOptions}
      styles={{
        container: (base) => ({
          ...base,
          minWidth: 220,
        }),
      }}
      value={selectedValue}
      getOptionLabel={(option) => option.name}
      getOptionValue={(option) => option.id}
      onChange={(selectedOption: SingleValue<CustomFilter.ItemValue>) => {
        onValuesChange(selectedOption ? [selectedOption] : []);
      }}
      isMulti={false}
      placeholder="Search territories..."
    />
  );
};